import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { FC, useState } from 'react';

import { ReactComponent as CompletedIcon } from '../../../../assets/icons/completed.svg';
import { getOrdinal } from '../../../../services/utils';
import AttachmentList from '../../../../shared/components/AttachmentList';
import { MarkdownEditor } from '../../../../shared/MarkdownEditor';
import { Attachment } from '../../../../types/work';
import {
    AcceptedTaskButton,
    AttachmentsWrapper,
    ChevronButton,
    CompletedTask,
    DescriptionContainer,
    IterationContainer,
    IterationTitle,
    LeadingGroup,
    StyledPaper,
    TaskHeader,
    TaskTitle,
    Title
} from './styled';

export interface CompletedTaskWithAttachments {
    id: string;
    title: string;
    description: string;
    attachments: Attachment[];
}

export interface CompletedIterationData {
    iterationNumber: number;
    tasks: CompletedTaskWithAttachments[];
}

interface QuestReviewProofOfWorkProps {
    completedTasksWithAttachments: CompletedIterationData[];
}

interface CompletedTaskRowProps {
    task: CompletedTaskWithAttachments;
    index: number;
}

const CompletedTaskRow: FC<CompletedTaskRowProps> = ({ task, index }) => {
    const [ expanded, setExpanded ] = useState(false);
    const hasDescription = Boolean(task.description && task.description.trim());

    return (
        <CompletedTask>
            <TaskHeader>
                <LeadingGroup>
                    <ChevronButton
                        expanded={expanded}
                        onClick={() => setExpanded((v) => !v)}
                        disabled={!hasDescription}
                        aria-expanded={expanded}
                        aria-label={
                            expanded ? 'Hide task description' : 'Show task description'
                        }
                        size="small"
                    >
                        <ChevronRightIcon fontSize="small" />
                    </ChevronButton>

                    <TaskTitle variant="body2">
                        {`#${index + 1} ${task.title}`}
                    </TaskTitle>
                </LeadingGroup>

                {task.attachments && task.attachments.length > 0 && (
                    <AttachmentsWrapper>
                        <AttachmentList
                            attachments={task.attachments}
                            showTitle={false}
                            withPadding={false}
                            withMargin={false}
                        />
                    </AttachmentsWrapper>
                )}

                <AcceptedTaskButton>
                    <CompletedIcon width={16} height={16} />
                    completed
                </AcceptedTaskButton>
            </TaskHeader>

            {expanded && hasDescription && (
                <DescriptionContainer>
                    <MarkdownEditor value={task.description} readOnly />
                </DescriptionContainer>
            )}
        </CompletedTask>
    );
};

const QuestReviewProofOfWork: FC<QuestReviewProofOfWorkProps> = ({
    completedTasksWithAttachments
}) => {
    if (
        !completedTasksWithAttachments ||
        completedTasksWithAttachments.length === 0
    ) {
        return null;
    }

    return (
        <StyledPaper elevation={0}>
            <Title variant="h6">Proof of work - atomic tasks</Title>

            {completedTasksWithAttachments.map(({ iterationNumber, tasks }) => (
                <IterationContainer key={`quest-review-${iterationNumber}`}>
                    <IterationTitle variant="subtitle1">
                        {getOrdinal(iterationNumber)} iteration
                    </IterationTitle>

                    {tasks.map((task, index) => (
                        <CompletedTaskRow
                            key={`task-${task.id}`}
                            task={task}
                            index={index}
                        />
                    ))}
                </IterationContainer>
            ))}
        </StyledPaper>
    );
};

export default QuestReviewProofOfWork;
